import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiHeart } = FiIcons;

const Results = () => {
  const stats = [
    {
      percentage: "20-30%",
      description: "increase in executive function skills",
      color: "from-accent-magenta to-accent-purple"
    },
    {
      percentage: "20%",
      description: "boost in cognitive performance with our sleep + EF combo",
      color: "from-accent-gold to-accent-yellow"
    },
    {
      percentage: "85%",
      description: "fall asleep faster within 4 weeks",
      color: "from-accent-lavender to-accent-purple"
    },
    {
      percentage: "92%",
      description: "report improved focus during the day",
      color: "from-green-400 to-emerald-500"
    }
  ];

  const testimonials = [
    {
      text: "My team noticed I'm sharper in meetings, and I actually have energy for my kids after work.",
      author: "Marcus T.",
      rating: 5
    },
    {
      text: "Finally ditched the melatonin. My brain just... works better now. Sleep comes naturally.",
      author: "Sarah K.",
      rating: 5
    }
  ];

  return (
    <section id="results" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            The Numbers That'll Keep You Up at Night (In a Good Way)
          </h2>
          <p className="text-xl text-gray-600 font-raleway">Our Clients See:</p>
        </motion.div>

        {/* Statistics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`bg-gradient-to-br ${stat.color} text-white rounded-3xl p-8 text-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105`}
            >
              <div className="text-4xl lg:text-5xl font-bold font-playfair mb-4">
                {stat.percentage}
              </div>
              <p className="text-white/90 font-raleway font-medium">
                {stat.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Let our customers speak for us:
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-accent-light/50 to-white rounded-3xl p-8 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-accent-gold fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 font-raleway italic leading-relaxed mb-6">
                  "{testimonial.text}"
                </blockquote>
                
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiHeart} className="w-6 h-6 text-accent-magenta" />
                  <cite className="font-semibold text-gray-900 not-italic">
                    — {testimonial.author}
                  </cite>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center space-y-6"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-accent-magenta/90 transition-all duration-200 shadow-xl"
            >
              Book Your Wake-Up Call
            </button>
            
            <a
              href="https://www.wethebillions.com/quiz"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-accent-magenta text-accent-magenta px-8 py-4 rounded-full font-semibold hover:bg-accent-magenta hover:text-white transition-all duration-200"
            >
              Take the Free Quiz
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Results;